import React, { FC, FocusEvent, useRef } from 'react';

import { ValueFieldProps, ValidationDataModels } from '@sitecore-jss/sitecore-jss-react-forms';
import { FileInputViewModel, ValidationDataModel, ValueFormField } from '@sitecore-jss/sitecore-jss-forms';
import { withTranslation, WithTranslation } from 'react-i18next';
import Icon from '../../../@shared/icon';
import useFirstUserAction from '../../hooks/useFirstUserActionState';
import CustomTextLabel from '../@shared/customTextLabel';

const SIZE_UNITS: { [key: string]: string } = {
  '1': 'Bytes',
  '1024': 'KB',
  '1048576': 'MB',
  '1073741824': 'GB',
};

const FileUploadField: FC<ValueFieldProps<FileInputViewModel> & WithTranslation> = (props) => {
  const { field, tracker, t } = props;
  const hasAnyError = !!props.errors?.length;
  const FieldValidationErrors = props.fieldValidationErrorsComponent;
  const label = field.model.title;
  const id = field.valueField.id;
  const name = field.valueField.name;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const triggerFirstInteractionGTMEvent = useFirstUserAction();

  const getEnabledValidation = (validatorItemId: string): ValidationDataModel | undefined =>
    field.model.validationDataModels.find((validation) => {
      return validation.itemId === validatorItemId;
    });

  const getFileSizeUnitName = (value: string): string => {
    return SIZE_UNITS[value];
  };
  const fileTypeValidator = getEnabledValidation(ValidationDataModels.FileTypeValidator);

  const onFocus = (e: FocusEvent<HTMLInputElement>): void => {
    tracker.onFocusField(field, e?.target?.value);
  };

  const onBlur = (e: FocusEvent<HTMLInputElement>): void => {
    tracker.onBlurField(field, e?.target?.value, []);
  };

  const onChange = (files: FileList | null, newfield: ValueFormField<FileInputViewModel>): void => {
    const fileSizeValidator = getEnabledValidation(ValidationDataModels.FileSizeValidator);
    const fileCountValidator = getEnabledValidation(ValidationDataModels.FileCountValidator);
    const list: Array<File> = [];
    const errorMessages: Array<string> = [];
    let valid = true;

    if (files) {
      Array(files.length)
        .fill(null)
        .forEach(function (_, idx) {
          const fileSize = files[idx].size / newfield.model.fileSizeUnit;
          if (valid && fileSizeValidator && fileSize > newfield.model.maxFileSize) {
            const msg = fileSizeValidator.message
              .replace('{0}', newfield.model.maxFileSize.toString())
              .replace('{1}', getFileSizeUnitName(newfield.model.fileSizeUnit.toString()));
            errorMessages.push(msg);
            valid = false;
          }
          list.push(files[idx]);
        });
    }
    if (fileTypeValidator) {
      list.some(function (file) {
        const ext = file.name.split('.').pop() || '';
        if (newfield.model.allowedContentTypes.indexOf(ext) !== -1) {
          return false;
        }
        const msg = fileTypeValidator.message.replace('{0}', newfield.model.allowedContentTypes);
        errorMessages.push(msg);
        return true;
      });
    }
    if (fileCountValidator && list.length > newfield.model.maxFileCount) {
      const msg = fileCountValidator.message.replace('{0}', newfield.model.maxFileCount.toString()).replace('{1}', newfield.model.title);
      errorMessages.push(msg);
      valid = false;
    }
    if (newfield.model.required && !list.length) {
      valid = false;
      errorMessages.push(newfield.model.title + ' is required');
    }
    props.onChange(newfield.valueField.name, list, valid, errorMessages);
    triggerFirstInteractionGTMEvent();
  };

  const onUploadClick = (): void => {
    inputRef?.current?.click();
  };

  const onClearClick = (): void => {
    if (inputRef.current) {
      inputRef.current.value = '';
      onChange(inputRef.current.files, field);
    }
  };

  return (
    <div
      className={`sitecore-jss-form__control sitecore-jss-form__control--file-upload ${
        hasAnyError ? 'sitecore-jss-form__control--has-error' : ''
      }`}
    >
      <label htmlFor={id} className="sitecore-jss-form__file-upload-label">
        <CustomTextLabel label={label} />
      </label>
      <div className="sitecore-jss-form__input-wrapper">
        <input
          id={id}
          name={name}
          type="file"
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={(e): void => onChange(e?.target?.files, field)}
          ref={inputRef}
          accept={fileTypeValidator ? field.model?.allowedContentTypes : undefined}
          multiple={field.model.isMultiple}
          className={`sitecore-jss-form__file-input ${!props?.value?.length ? 'sitecore-jss-form__file-input--is-empty' : ''}`}
        />
        <Icon iconName="action-delete-2" size={24} extraClassName="sitecore-jss-form__delete-icon" onClick={onClearClick} />
      </div>
      <button type="button" className="sitecore-jss-form__file-upload-btn" onClick={onUploadClick}>
        {t('cnhi-chooseFile')}
        <Icon iconName="action-cloud-upload" size={24} color="primary" extraClassName="sitecore-jss-form__upload-icon" />
      </button>

      {FieldValidationErrors && <FieldValidationErrors {...props} />}
    </div>
  );
};

export default withTranslation()(FileUploadField);
